import { Dialog, Transition } from "@headlessui/react";
import { DrawerSize } from "@via/components/table/table.tsx";
import { classNames } from "@via/utils/helpers.tsx";
import {
  type JSX,
  Fragment,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import {
  MinusIcon,
  ChevronRightIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

interface DrawerProps {
  id?: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  children?: JSX.Element;
  size?: string;
  className?: string;
  isMinimized?: boolean;
  onMinimize?: () => void;
  onRestore?: () => void;
  showMinimizeButton?: boolean;
  title?: string;
  allowBackgroundInteraction?: boolean;
  minimizedHighlight?: boolean;
  usePortal?: boolean;
  resizable?: boolean;
  onWidthChange?: (width: number | null) => void;
}

const DrawerContent = ({
  showMinimizeButton,
  onMinimize,
  title,
  children,
  setOpen,
  resizable,
  resizeHandle,
}: {
  showMinimizeButton: boolean;
  onMinimize?: () => void | undefined;
  title?: string | undefined;
  children: JSX.Element;
  setOpen: (open: boolean) => void;
  resizable?: boolean;
  resizeHandle?: JSX.Element | null;
}) => {
  return (
    <div className="flex h-full flex-col bg-white shadow-xl relative">
      {/* Resize Handle */}
      {resizable && resizeHandle && (
        <div className="absolute left-0 top-0 bottom-0 z-10">
          {resizeHandle}
        </div>
      )}
      {/* Full Header with Minimize Button */}
      {showMinimizeButton && onMinimize && (
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          {title && (
            <h3 className="text-lg font-semibold leading-6 text-gray-900">
              {title}
            </h3>
          )}
          <div className="ml-3 flex h-7 items-center gap-2">
            <button
              type="button"
              className="relative rounded-md text-gray-900 duration-150 ease-in-out hover:text-gray-600 focus:outline-none"
              onClick={onMinimize}
              title="Minimize drawer">
              <span className="absolute -inset-2.5" />
              <span className="sr-only">Minimize panel</span>
              <MinusIcon className="h-6 w-6" aria-hidden="true" />
            </button>
            <button
              type="button"
              className="relative rounded-md text-gray-900 duration-150 ease-in-out hover:text-gray-600 focus:outline-none"
              onClick={() => setOpen(false)}
              title="Close drawer">
              <span className="absolute -inset-2.5" />
              <span className="sr-only">Close panel</span>
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
        </div>
      )}
      {/* Drawer Content */}
      <div className="flex-1 overflow-hidden">{children}</div>
    </div>
  );
};

export default function Drawer({
  id = "",
  open,
  setOpen,
  children,
  size = DrawerSize.Small,
  className = "",
  isMinimized = false,
  onMinimize,
  onRestore,
  showMinimizeButton = true,
  title = "",
  allowBackgroundInteraction = false,
  minimizedHighlight = false,
  usePortal = true,
  resizable = false,
  onWidthChange,
}: DrawerProps): JSX.Element {
  const drawerId: string = id ? `drawer-${id}` : "";

  // Calculate minimum width based on size
  const getMinWidth = useCallback(() => {
    switch (size) {
      case DrawerSize.Small:
        return 448; // 28rem
      case DrawerSize.Medium:
        return 512; // 32rem
      case DrawerSize.Big:
        return 768; // 48rem
      default:
        return 448;
    }
  }, [size]);

  const minWidth = getMinWidth();
  const maxWidth =
    typeof window !== "undefined" ? window.innerWidth * 0.8 : 1200;

  // State for drawer width
  const [drawerWidth, setDrawerWidth] = useState<number | null>(null);
  const isResizingRef = useRef(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  // Initialize width when resizable is enabled
  useEffect(() => {
    if (resizable && drawerWidth === null && open && !isMinimized) {
      setDrawerWidth(minWidth);
      onWidthChange?.(minWidth);
    }
  }, [resizable, drawerWidth, open, isMinimized, minWidth, onWidthChange]);

  // Reset width when drawer closes or becomes non-resizable
  useEffect(() => {
    if (!resizable || !open) {
      setDrawerWidth(null);
      onWidthChange?.(null);
    }
  }, [resizable, open, onWidthChange]);

  // Notify parent of width changes
  useEffect(() => {
    if (resizable && !isMinimized) {
      onWidthChange?.(drawerWidth);
    } else if (isMinimized) {
      onWidthChange?.(null);
    }
  }, [drawerWidth, resizable, isMinimized, onWidthChange]);

  // Handle resize move - works with both MouseEvent and PointerEvent
  const handleResizeMove = useCallback(
    (e: { clientX: number }) => {
      if (!isResizingRef.current) return;
      const deltaX = startXRef.current - e.clientX; // Negative delta means dragging left (increasing width)
      const newWidth = Math.max(
        minWidth,
        Math.min(maxWidth, startWidthRef.current + deltaX)
      );
      setDrawerWidth(newWidth);
    },
    [minWidth, maxWidth]
  );

  // Handle resize end
  const handleResizeEnd = useCallback(() => {
    if (!isResizingRef.current) return;
    isResizingRef.current = false;
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  }, []);

  // Handle resize start - uses Pointer Events with setPointerCapture so that
  // pointerup is delivered even when the cursor moves over an iframe (e.g.
  // report viewer). Without this, mouseup would be lost when released over
  // an iframe, leaving the drawer stuck in resize mode.
  const handleResizeStart = useCallback(
    (e: React.PointerEvent) => {
      if (isMinimized || !resizable) return;
      e.preventDefault();
      e.stopPropagation();
      const target = e.currentTarget as HTMLElement;
      target.setPointerCapture(e.pointerId);
      isResizingRef.current = true;
      startXRef.current = e.clientX;
      startWidthRef.current = drawerWidth || minWidth;
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";

      const handlePointerMove = (moveEvent: PointerEvent) => {
        handleResizeMove(moveEvent);
      };

      const cleanup = () => {
        if (!isResizingRef.current) return;
        handleResizeEnd();
        try {
          target.releasePointerCapture(e.pointerId);
        } catch {
          /* ignore if already released */
        }
        target.removeEventListener("pointermove", handlePointerMove);
        target.removeEventListener("pointerup", cleanup);
        target.removeEventListener("pointercancel", cleanup);
        window.removeEventListener("pointerup", cleanup);
        window.removeEventListener("blur", cleanup);
        document.removeEventListener("visibilitychange", cleanup);
      };

      // Fallback: window-level listeners for edge cases (e.g. pointer capture
      // lost when over iframe in some browsers, window blur, tab switch)
      window.addEventListener("pointerup", cleanup, { capture: true });
      window.addEventListener("blur", cleanup);
      document.addEventListener("visibilitychange", cleanup);

      target.addEventListener("pointermove", handlePointerMove);
      target.addEventListener("pointerup", cleanup);
      target.addEventListener("pointercancel", cleanup);
    },
    [
      isMinimized,
      resizable,
      drawerWidth,
      minWidth,
      handleResizeMove,
      handleResizeEnd,
    ]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, []);

  // Create resize handle - use onPointerDown for setPointerCapture support
  // (ensures pointerup fires even when cursor moves over iframes like report viewer)
  const resizeHandle =
    resizable && !isMinimized ? (
      <div
        onPointerDown={handleResizeStart}
        className="absolute left-0 top-0 bottom-0 w-1 cursor-col-resize bg-transparent hover:bg-indigo-300 transition-colors duration-150 group z-20 touch-none"
        aria-label="Resize drawer"
        role="separator"
        aria-orientation="vertical">
        <div className="absolute left-0 top-0 bottom-0 w-1 group-hover:w-1.5 transition-all duration-150" />
      </div>
    ) : null;

  const renderMinimizedBar = () => (
    <div
      className={classNames(
        "w-16 max-w-16 h-full pointer-events-auto transition-all duration-300 ease-in-out shadow-xl border-l rounded-l-lg py-4",
        minimizedHighlight
          ? "bg-indigo-300 ring-2 ring-indigo-300 animate-pulse border-indigo-300"
          : "bg-white border-gray-200"
      )}>
      <div className="flex flex-col h-full items-center gap-4">
        <div className="flex flex-col items-center gap-2">
          {onRestore && (
            <button
              type="button"
              className="relative rounded-md text-gray-900 duration-150 ease-in-out hover:text-gray-600 focus:outline-none p-2"
              onClick={onRestore}
              title="Restore drawer">
              <span className="absolute -inset-2.5" />
              <span className="sr-only">Restore panel</span>
              <ChevronRightIcon
                className="h-5 w-5 rotate-180"
                aria-hidden="true"
              />
            </button>
          )}
          <button
            type="button"
            className="relative rounded-md text-gray-900 duration-150 ease-in-out hover:text-gray-600 focus:outline-none p-2"
            onClick={() => setOpen(false)}
            title="Close drawer">
            <span className="absolute -inset-2.5" />
            <span className="sr-only">Close panel</span>
            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        {title && (
          <div
            className="text-xs font-medium text-gray-700 max-w-[48px] flex-1 flex items-center justify-center"
            style={{
              writingMode: "vertical-rl",
              textOrientation: "mixed",
            }}>
            {title}
          </div>
        )}
      </div>
    </div>
  );

  // Inline mode (no portal) mimics side navigation behavior
  if (!usePortal) {
    if (!open && !isMinimized) {
      return <></>;
    }

    return (
      <div className={classNames("relative h-full w-full", className)}>
        <Transition.Root show={open && !isMinimized} as={Fragment}>
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute inset-y-0 right-0 flex h-full w-full">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom="translate-x-full opacity-0"
                enterTo="translate-x-0 opacity-100"
                leave="transform transition ease-in-out duration-300"
                leaveFrom="translate-x-0 opacity-100"
                leaveTo="translate-x-full opacity-0">
                <div
                  className={classNames(
                    resizable
                      ? "w-full"
                      : size === DrawerSize.Small
                        ? "max-w-md w-full"
                        : size === DrawerSize.Medium
                          ? "max-w-lg w-full"
                          : "w-full",
                    "pointer-events-auto h-full bg-white shadow-xl"
                  )}>
                  <DrawerContent
                    showMinimizeButton={showMinimizeButton}
                    onMinimize={onMinimize}
                    title={title}
                    setOpen={setOpen}
                    resizable={resizable}
                    resizeHandle={resizeHandle ?? undefined}>
                    {children ?? <></>}
                  </DrawerContent>
                </div>
              </Transition.Child>
            </div>
          </div>
        </Transition.Root>

        {isMinimized && open && (
          <div className="absolute inset-0 flex items-start justify-end">
            {renderMinimizedBar()}
          </div>
        )}
      </div>
    );
  }

  // When minimized, render without Dialog wrapper to allow background interaction
  if (isMinimized) {
    const minimizedContent = (
      <div
        className={classNames(
          "fixed inset-0 z-[99] pointer-events-none",
          className
        )}>
        <div
          id={drawerId}
          className="pointer-events-none fixed right-0 top-0 bottom-0 flex items-start">
          {renderMinimizedBar()}
        </div>
      </div>
    );

    if (typeof document !== "undefined") {
      return createPortal(minimizedContent, document.body);
    }
    return minimizedContent;
  }

  // Non-blocking drawer (allows background interaction)
  if (allowBackgroundInteraction) {
    const dynamicWidthStyle =
      resizable && drawerWidth !== null && !isMinimized
        ? { width: `${drawerWidth}px` }
        : undefined;

    const drawerContent = (
      <Transition.Root show={open} as={Fragment}>
        <div
          className={classNames(
            "pointer-events-none fixed inset-0 z-[99]",
            className
          )}>
          {/* Backdrop */}
          <Transition.Child
            as={Fragment}
            enter="ease-in-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0">
            <div className="fixed inset-0  pointer-events-none" />
          </Transition.Child>

          {/* Drawer Container */}
          <div className="fixed inset-0 overflow-hidden pointer-events-none">
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div
                id={drawerId}
                className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-in-out duration-500 sm:duration-300"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition ease-in-out duration-500 sm:duration-300"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full">
                  <div
                    className={classNames(
                      resizable
                        ? ""
                        : size === DrawerSize.Small
                          ? "max-w-md"
                          : size === DrawerSize.Medium
                            ? "max-w-lg"
                            : "",
                      "pointer-events-auto w-screen transition-all duration-300 ease-in-out"
                    )}
                    style={dynamicWidthStyle}>
                    <DrawerContent
                      showMinimizeButton={showMinimizeButton}
                      onMinimize={onMinimize}
                      title={title}
                      setOpen={setOpen}
                      resizable={resizable}
                      resizeHandle={resizeHandle}>
                      {children ?? <></>}
                    </DrawerContent>
                  </div>
                </Transition.Child>
              </div>
            </div>
          </div>
        </div>
      </Transition.Root>
    );

    if (typeof document !== "undefined") {
      return createPortal(drawerContent, document.body);
    }
    return drawerContent;
  }

  // Original blocking drawer (backward compatible)
  const dynamicWidthStyle =
    resizable && drawerWidth !== null && !isMinimized
      ? { width: `${drawerWidth}px` }
      : undefined;

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        static={true}
        as="div"
        className={classNames("relative", className || "z-[99]")}
        onClose={setOpen}>
        <Transition.Child
          as={Fragment}
          enter="ease-in-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>
        <div className="fixed inset-0" />

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div
              id={drawerId}
              className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-300"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-300"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full">
                <Dialog.Panel
                  className={classNames(
                    resizable
                      ? ""
                      : size === DrawerSize.Small
                        ? "max-w-md"
                        : size === DrawerSize.Medium
                          ? "max-w-lg"
                          : "",
                    "pointer-events-auto w-screen transition-all duration-300 ease-in-out"
                  )}
                  style={dynamicWidthStyle}>
                  <DrawerContent
                    showMinimizeButton={showMinimizeButton}
                    onMinimize={onMinimize}
                    title={title}
                    setOpen={setOpen}
                    resizable={resizable}
                    resizeHandle={resizeHandle ?? undefined}>
                    {children ?? <></>}
                  </DrawerContent>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
